// Tanner Fry
// tannerf1101@yahoo.com
// Common functions used throughout the app.

import { useEffect } from 'react';
import { RGBColor } from 'react-color';
import { APIPOSTSubscriptionsFeatureAuth } from '../../api/Common';
import { SubscriptionPlan } from './data/SubscriptionData';
import { AppFeatureType, AppSubFeatureType, IClearanceResponse } from './privateRoute/SecurityClearance';

const useScript = url => {
	useEffect(() => {
		const script = document.createElement('script');
		
		script.src = url;
		script.async = true;
		
		document.body.appendChild(script);
		
		return () => {
			document.body.removeChild(script);
		}
	}, [url]);
};

export const TruncateString = (str, num) => {
	// If the length of str is less than or equal to num
	// just return str--don't truncate it.
	if (str.length <= num) {
		return str
	}

	// Return str truncated with '...' concatenated to the end of str.
	return str.slice(0, num) + '...'
}

export const CommaSeparateList = (list) => {
	let listString = '';

	let length = list.length;
	let i = 0;
	list.forEach((item) => {
		listString += item;
		if (i < length - 1) {
			listString += ', ';
		}
		i++;
	});

	return listString;
}

export const TruncateDecimals = (number, digits) => {
	let multiplier = Math.pow(10, digits),
	adjustedNum = number * multiplier,
	truncatedNum = Math[adjustedNum < 0 ? 'ceil' : 'floor'](adjustedNum);
	
	return truncatedNum / multiplier;
};

export const elementIsVisibleInViewport = (el, partiallyVisible = false) => {
	const { top, left, bottom, right } = el.getBoundingClientRect();
	const { innerHeight, innerWidth } = window;

	return partiallyVisible ? (
		(top > 0 && top < innerHeight) || (bottom > 0 && bottom < innerHeight))
		&& ((left > 0 && left < innerWidth) || (right > 0 && right < innerWidth)
	) : (
		top >= 0 && left >= 0 && bottom <= innerHeight && right <= innerWidth
	);
};

export const checkSizeIsMobile = (size) => {
	if (size < 768 && size > 0) {
		return true;
	} else {
		return false;
	}
};

export const isOverflownHeight = (element) => {
	return element.scrollHeight > element.clientHeight;
}

export const isOverflownWidth = (element) => {
	return element.scrollWidth > element.clientWidth;
}

export const componentToHex = (c) => {
	let hex = c.toString(16);
	return hex.length == 1 ? "0" + hex : hex;
}
  
export const rgbToHex = (r, g, b) => {
	return "#" + componentToHex(r) + componentToHex(g) + componentToHex(b);
}

export const hexToRgb = (hex) => {
	let result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
	return result ? {
	r: parseInt(result[1], 16),
	g: parseInt(result[2], 16),
	b: parseInt(result[3], 16)
	} : null;
}

export const rgbaStringToRgbaColor = (rgbString: string): RGBColor | null => {
	// String should be in the format of rgba(255, 255, 255, 1)
	// Verify it's rgba:
	if (!rgbString.includes('rgba(')) {
		return null;
	}
	
	const rgbStringArray = rgbString.replace('rgba(', '').replace(')', '').split(',');
	const r = parseInt(rgbStringArray[0]);
	const g = parseInt(rgbStringArray[1]);
	const b = parseInt(rgbStringArray[2]);
	const a = parseFloat(rgbStringArray[3]);

	return {
		r: r,
		g: g,
		b: b,
		a: a
	};
}

export const rgbaColorToRgbaString = (rgbColor: RGBColor): string | null => {
	// Verify it's rgba:
	if (rgbColor === null || rgbColor === undefined) {
		return null;
	}
	
	return `rgba(${rgbColor.r}, ${rgbColor.g}, ${rgbColor.b}, ${rgbColor.a})`;
}

export const rgbaColorToHex = (rgbaColor: RGBColor): string => {
	if (rgbaColor === null || rgbaColor === undefined) {
		return '';
	}

	return rgbToHex(rgbaColor.r, rgbaColor.g, rgbaColor.b);
}

// https://stackoverflow.com/questions/3115150/how-to-escape-regular-expression-special-characters-using-javascript
export const escapeRegExpSpecialCharacters = (text: string) => {
	if (typeof text === 'string' && text !== null && text !== undefined) {
		return text.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
	} else {
		return text;
	}
}

// TODO: Enhance by utilizing complimentary colors.
export const invertColor = (hex, bw) => {
	if (hex === '') {
		return '';
	}
	if (hex.indexOf('#') === 0) {
		hex = hex.slice(1);
	}
	// convert 3-digit hex to 6-digits.
	if (hex.length === 3) {
		hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
	}
	if (hex.length !== 6) {
		console.log(hex);
		
		throw new Error('Invalid HEX color.');
	}
	let r = parseInt(hex.slice(0, 2), 16),
		g = parseInt(hex.slice(2, 4), 16),
		b = parseInt(hex.slice(4, 6), 16);
	if (bw) {
		// https://stackoverflow.com/a/3943023/112731
		return (r * 0.299 + g * 0.587 + b * 0.114) > 186
			? '#000000'
			: '#FFFFFF';
	}
	// invert color components
	let rStr = (255 - r).toString(16),
		gStr = (255 - g).toString(16),
		bStr = (255 - b).toString(16);
	// pad each with zeros and return
	return "#" + padZero(rStr, bw) + padZero(gStr, bw) + padZero(bStr, bw);
}

export const padZero = (str, len) => {
	len = len || 2;
	let zeros = new Array(len).join('0');
	return (zeros + str).slice(-len);
}

export const offsetRGBColor = (color: RGBColor, offset: number): RGBColor => {
	// Check if offsetting the color will make it go out of bounds. 0 - 255
	const offsetComponent = (component: number): number => {
        const newComponent = component + offset;
        if (newComponent > 255) return component - offset;
        if (newComponent < 0) return component + offset;
        return newComponent;
    };

    return {
        r: offsetComponent(color.r),
        g: offsetComponent(color.g),
        b: offsetComponent(color.b),
        a: color.a
    };
}

export const escapeRegExpSpecialCharactersDoubleQuotesSingleQuotes = (text: string) => {
	console.log("Text: ", text);
	console.log("Text type: ", typeof text);
	console.log("Text null: ", text === null);
	console.log("Text undefined: ", text === undefined);
	
	
	
	
	if (typeof text === 'string' && text !== null && text !== undefined) {
		console.log("Replacing text: ", text);
		let newText = text.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
		console.log("New text: ", newText);
		
		
		return text.replace(/[-[\]{}()*+?.,\\^$|#\s"']/g, '\\$&');
	} else {
		return text;
	}
}

export const replaceEscapedQuotes = (text: string): string => {
	let newText = text;
	if (typeof text === 'string' && text !== null && text !== undefined) {
		newText = text.replace(/&quot;/g, '"');
		newText = newText.replace(/&#039;/g, "'");
		newText = newText.replace(/&#x27;/g, "'");
		newText = newText.replace(/&amp;/g, '&');
		newText = newText.replace(/&lt;/g, '<');
		newText = newText.replace(/&gt;/g, '>');
		newText = newText.replace(/&#x2F;/g, '/');
	}
	return newText;
}

// Return a string of the plan features
export const returnPlanFeatures = (plan: SubscriptionPlan): string => {
	let string = '';
	if (plan === SubscriptionPlan.Free) {
		string = '5 Animals, Task Tracking, Gardening Wiki, Maintenance Tracking, Weather Data, and Work Time Tracking'
	} else if (plan === SubscriptionPlan.Base) {
		string = '25 Animals, Bill Tracking, Cooking/Recipes/Groceries, Gardening Wiki, '
					 + 'Hobby Tracking (Fishing/Hunting), Maintenance Tracking, Notes, '
					 + 'Task Tracking, Weather Data, Weather Radar, Work Time Tracking';
	} else if (plan === SubscriptionPlan.Packed) {
		string = 'Unlimited Animals, Bill Tracking, Cooking/Recipes/Groceries, Encrypted Notes, '
					 + 'Garden Layout Builder, Gardening Wiki, Hobby Tracking (Fishing/Hunting), '
					 + ' Maintenance Tracking, Notes, Task Tracking, Weather Data, Weather Radar, Work Time Tracking';
	}

	return string;
}

// Checks if a particular user has clearance for a feature
export const returnHasClearanceForFeature = async (featureType: AppFeatureType, subFeatureType?: AppSubFeatureType) => {
	let doesHaveClearance = false;
	if (localStorage.getItem('user') !== null) {
		const hasClearance = async (featureType: AppFeatureType, subFeatureType?: AppSubFeatureType) => {       
			const clearanceResponse: IClearanceResponse = await APIPOSTSubscriptionsFeatureAuth(JSON.parse(localStorage.getItem('user')).uuid, AppFeatureType[featureType], AppSubFeatureType[subFeatureType]);
			console.log("Clearance Response: ", clearanceResponse);
			
			if (clearanceResponse.data !== undefined && clearanceResponse.data.isCleared) {
				doesHaveClearance = true;
			} else {
				doesHaveClearance = false;
			}
		};

		await hasClearance(featureType, subFeatureType);
	}
	return doesHaveClearance;
}
  

export default useScript;